<script setup lang="ts">
  import { Execution } from '@/shared/Executions/models/Execution'
  import {
    AdvancedAnalysisPhaseType,
    ExecutionPhaseType
  } from '@/shared/Executions/models/ExecutionPhaseType'
  import { QuestionOutlined } from '@ant-design/icons-vue'
  import { BookText, Calculator, Cog, Sparkles } from 'lucide-vue-next'
  import { computed } from 'vue'
  import VisulizationTypeIcon from './VisualizationTypeIcon.vue'
  import { IExecutionPhaseService } from '@/shared/Executions/services/ExecutionPhaseService'
  import AdvancedAnalysisTypeIcon from './AdvancedAnalysisTypeIcon.vue'

  const props = defineProps<{
    record: Execution
    index?: number
    selectable?: boolean
    onCellClick?: (record: Execution, e?: Event) => void
    excecutionPhaseService: IExecutionPhaseService
  }>()

  function handleClick(e?: Event) {
    if (props.selectable && props.onCellClick) {
      props.onCellClick(props.record, e)
    }
  }

  const primaryPhase = computed(() => {
    if ((props.record?.progress?.totalPhases ?? 0) > 1) {
      return AdvancedAnalysisPhaseType
    } else {
      return props.excecutionPhaseService.tryGetPrimaryPhaseTypeForExecution(props.record)
    }
  })

  const executionTypeIcon = computed(() => {
    switch (primaryPhase.value) {
      case ExecutionPhaseType.DataCleaning:
        return Sparkles
      case ExecutionPhaseType.NCAAnalysis:
        return Cog
      case ExecutionPhaseType.CustomCalculations:
        return Calculator
      case ExecutionPhaseType.Visualization:
        return VisulizationTypeIcon
      case ExecutionPhaseType.Reports:
        return BookText
      case AdvancedAnalysisPhaseType:
        return AdvancedAnalysisTypeIcon
      default:
        QuestionOutlined
    }
  })

  const iconProps = computed(() =>
    executionTypeIcon.value === VisulizationTypeIcon || AdvancedAnalysisPhaseType
      ? {
          record: props.record,
          index: props.index,
          selectable: props.selectable,
          onCellClick: props.onCellClick,
          excecutionPhaseService: props.excecutionPhaseService
        }
      : {}
  )

  const executionTooltip = computed(() => {
    switch (primaryPhase.value) {
      case ExecutionPhaseType.DataCleaning:
        return 'Data Cleaning'
      case ExecutionPhaseType.NCAAnalysis:
        return 'NCA Analysis'
      case ExecutionPhaseType.CustomCalculations:
        return 'Custom Calculations'
      case ExecutionPhaseType.Reports:
        return 'Custom Report'
      case AdvancedAnalysisPhaseType:
        return 'Advanced Analysis'
      default:
        'Not Available'
    }
  })
</script>

<template>
  <div :class="{ 'cursor-pointer': selectable }" @click="handleClick">
    <a-tooltip
      v-if="
        primaryPhase !== ExecutionPhaseType.Visualization &&
        primaryPhase !== AdvancedAnalysisPhaseType
      "
    >
      <template #title>
        <div>
          <div>
            <b>Type:</b>
            {{ executionTooltip }}
          </div>
        </div>
      </template>
      <component :is="executionTypeIcon" v-bind="iconProps" class="size-4" />
    </a-tooltip>
    <component v-else :is="executionTypeIcon" v-bind="iconProps" class="size-4" />
  </div>
</template>
